import { Fragment, useContext, useEffect, useState } from 'react'

import classNames from 'classnames'
import { CollectionContents, CollectionPageContents_me } from 'globalTypes'

import { useMutation } from '@apollo/client'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Trans, msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import Button from 'Root/App/Button'
import CreateContent from 'Root/App/Button/CreateContent'
import { findFirstUncompletedModulePosition } from 'Root/App/Item/Module/findFirstUncompletedModulePosition'
import Module from 'Root/App/Item/Module/index'
import SortableModuleWrapper from 'Root/App/Item/SortableModuleWrapper'
import Title from 'Root/App/Title'
import { useFeatureEnabledByDate } from 'lib/use-feature-enabled-by-date'
import map from 'lodash/map'

import { Context as AssociationContext } from '../AssociationContext'
import Item from './Item'
import SortableWrapper from './SortableWrapper'
import MUTATION from './create-module.graphql'
import styles from './index.module.css'

// ----------------------------------------------------------------------------

library.add(faPlus)

// ----------------------------------------------------------------------------

export interface Props extends CollectionContents {
  refetchOnUpdate: Function
  me?: CollectionPageContents_me | null
  slug: string
  canRead: boolean
  canManage: boolean
  modules?: any
  preparatory?: string
}

const Contents = (props) => {
  const { _ } = useLingui()
  const [loading, featureIsEnabledForThisUser] = useFeatureEnabledByDate()
  const { setCollection } = useContext(AssociationContext)

  const contents = map(props?.contents?.edges || [], 'node')
  const collectionId = props.id
  const modules = map(props?.modules?.edges || [], 'node')

  const [mappedData, setMappedData] = useState([])

  const showContents = modules.length === 0

  const [mutate] = useMutation(MUTATION)

  const onClick = () => {
    return setCollection({ id: collectionId, visibility: props.visibility })
  }

  useEffect(() => {
    if (props.preparatory === 'contents') {
      if (contents.length > 0) {
        const contentsData = contents.map((content) => ({
          ...content,
        }))

        contentsData[0].isUnlocked = true
        for (let i = 1; i < contentsData.length; i++) {
          contentsData[i].isUnlocked = getIsUnlocked(i, contentsData)
        }
        setMappedData(contentsData)
      }

      if (modules.length > 0) {
        //set module content data to handle preparatory
        const modulesData = modules.map((module) => ({
          ...module,
          contents: {
            edges: module.contents?.edges?.map((edge) => ({ ...edge })),
          },
        }))

        let firstUnlocked = true
        const newModules = []
        for (const module of modulesData) {
          const localContents = module.contents.edges.map((edge) => ({
            ...edge,
          }))
          if (localContents.length > 0) {
            if (!!firstUnlocked) {
              localContents[0].node = {
                ...localContents[0].node,
                isUnlocked: true,
              }
            } else {
              localContents[0].node = {
                ...localContents[0]?.node,
                isUnlocked: false,
              }
            }

            for (let i = 1; i < localContents?.length; i++) {
              const newNode = {
                ...localContents[i].node,
                isUnlocked: getIsUnlocked(i, map(localContents || [], 'node')),
              }
              localContents[i].node = newNode
              firstUnlocked =
                newNode.isUnlocked &&
                (newNode.hasUserCompleted || newNode.hasUserVisited)
            }
            newModules.push({
              ...module,
              contents: { edges: localContents.map((node) => ({ ...node })) },
            })
          }
        }
        setMappedData(newModules)
      }
    }
  }, [contents])

  useEffect(() => {
    if (props.preparatory === 'modules') {
      if (modules.length > 0) {
        const modulesData = modules.map((module) => ({
          ...module,
        }))

        modulesData[0].isUnlocked = true
        for (let i = 1; i < modulesData.length; i++) {
          modulesData[i].isUnlocked = getModuleIsUnlocked(i, modulesData)
        }

        setMappedData(modulesData)
      }
    }
  }, [modules])

  function getIsUnlocked(index, mappedContents) {
    if (index > 0) {
      return (
        mappedContents[index - 1]?.isUnlocked &&
        (!!mappedContents[index - 1]?.hasUserCompleted ||
          !!mappedContents[index - 1]?.hasUserVisited)
      )
    }
    return true
  }

  function getModuleIsUnlocked(index, mappedModules) {
    if (index > 0) {
      return (
        mappedModules[index - 1]?.isUnlocked &&
        isModuleCompleted(mappedModules[index - 1])
      )
    }
    return true
  }

  function isModuleCompleted(module) {
    let completed = 0
    for (const content of module.contents.edges) {
      if (content.node.hasUserVisited || content.node.hasUserCompleted) {
        completed++
      }
    }
    return completed === module.contents.edges.length
  }

  async function addModule() {
    await mutate({
      variables: {
        collectionId,
        input: { title: _(msg`Titolo del modulo`) },
      },
    })

    props.refetchOnUpdate()
  }

  if (loading) {
    return null
  }

  return (
    <div
      className={classNames([
        styles.wrapper,
        modules.length > 0 || contents.length > 0 ? 'p-6' : 'p-0',
      ])}
    >
      {(modules.length > 0 || contents.length > 0) && (
        <Title>
          <Trans>Contenuti</Trans>
        </Title>
      )}

      {!props.canManage && modules.length > 0 ? (
        map(
          props?.preparatory === 'contents' || props?.preparatory === 'modules'
            ? mappedData
            : modules,
          (module) => (
            <Module
              key={module.id}
              module={module}
              collectionId={collectionId}
              collectionSlug={props.slug}
              collectionName={props.title}
              visibility={props.visibility}
              canReadCollection={props.canRead}
              canManage={props.canManage}
              refetchOnUpdate={props.refetchOnUpdate}
              openOnStart={
                module.position === findFirstUncompletedModulePosition(modules)
              }
            />
          )
        )
      ) : (
        <Fragment>
          <SortableModuleWrapper
            collectionId={collectionId}
            modules={modules}
            collectionSlug={props.slug}
            canReadCollection={props.canRead}
            canManageCollection={props.canManage}
            refetchOnUpdate={props.refetchOnUpdate}
            visibility={props.visibility}
          />
        </Fragment>
      )}

      {!!showContents &&
        (!props.canManage
          ? map(
              props?.preparatory === 'contents' ? mappedData : contents,
              (content) => (
                <Item
                  key={content.id}
                  content={content}
                  collectionSlug={props.slug}
                  collectionId={props.id}
                  canReadCollection={props.canRead}
                  canManageCollection={props.canManage}
                  refetchOnUpdate={props.refetchOnUpdate}
                  collectionVisibility={props.visibility}
                />
              )
            )
          : props.canManage && (
              <Fragment>
                <SortableWrapper
                  collectionId={collectionId}
                  contents={contents}
                  collectionSlug={props.slug}
                  canReadCollection={props.canRead}
                  canManageCollection={props.canManage}
                  refetchOnUpdate={props.refetchOnUpdate}
                  collectionVisibility={props.visibility}
                />
              </Fragment>
            ))}

      {props.canManage && (
        <div className="flex gap-x-5 mt-8">
          {modules?.length === 0 && (
            <Button onClick={onClick} className="bg-gray-100 text-gray-500">
              <div>
                <Trans>Associa contenuti</Trans>{' '}
                <FontAwesomeIcon icon={['fas', 'plus']} />
              </div>
            </Button>
          )}
          {modules?.length === 0 && !featureIsEnabledForThisUser && (
            <CreateContent collectionSlug={props.slug} />
          )}
          <Button onClick={addModule}>
            <div>
              <Trans>Aggiungi modulo</Trans>{' '}
              <FontAwesomeIcon icon={['fas', 'plus']} />
            </div>
          </Button>
        </div>
      )}
    </div>
  )
}

export default Contents
